'use client';

import { get as _get, includes as _includes, isFinite as _isFinite } from 'lodash-es';
import { useEffect, Fragment, useMemo } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useQuery, useSuspenseQuery } from '@apollo/client/react';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Star from '@mui/icons-material/Star';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import Loading from '@/components/loading';
import ErrorMessage from '@/components/error-message';
import EventList from '@/components/event-list';
import DatedEventList from '@/components/dated-event-list';
import { groupQuery, groupInviteQuery } from '@/apollo/groups';
import JoinButton from '@/components/groups/join-button';
import UnarchiveGroupButton from '@/components/groups/unarchive-group-button';
import LatestPost from '@/components/groups/latest-post';
import SocialLinks from '@/components/social-links';
import Markdown from '@/components/markdown';
import MaterialLinkButton from '@/components/material-link-button';
import ImageUpload from '@/components/image-upload';
import SectionHeader from '@/components/section-header';
import { buildImageUrl } from '@/utils/images';
import GroupImageUploads from '@/components/groups/image-uploads';
import EventSeriesSection from '@/components/groups/event-series-section';

const useStyles = makeStyles()((theme) => ({
  main: {
    margin: theme.spacing(2, 0, 0, 0),
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  about: {
    flexGrow: 1,
  },
  description: {
    '& > *:first-of-type': {
      marginTop: 0,
    },
  },
  image: {
    maxWidth: 300,
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      maxWidth: '100%',
    },
  },
  eventsHeaderRight: {
    display: 'flex',
  },
  buttons: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  otherEventsButton: {
    marginLeft: theme.spacing(1),
  },
  chipsWrapper: {
    margin: theme.spacing(0, 0, 1, 0),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(0.5),
    },
  },
  featuredHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  featuredIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 24,
    color: theme.palette.secondary.main,
  },
  eventList: {
    marginTop: theme.spacing(2),
  },
  inviteButton: {
    marginTop: theme.spacing(1),
  },
}));

type Props = {
  joinToken?: string;
};

const GroupDetailPageClient = ({ joinToken }: Props) => {
  const { classes } = useStyles();
  const { slug } = useParams();
  const searchParams = useSearchParams();
  const joinParam = joinToken || searchParams.get('join');

  const { data: groupData, refetch } = useSuspenseQuery(groupQuery, {
    variables: { slug },
  });
  const group = _get(groupData, 'group');

  const {
    data: groupInviteData,
    loading: groupInviteLoading,
    error: groupInviteError,
  } = useQuery(groupInviteQuery, {
    variables: { id: joinParam },
    skip: !joinParam,
  });
  const groupInvite = _get(groupInviteData, 'groupInvite');

  useEffect(() => {
    if (group && group.image && group.permissions.editable) {
      new Image().src = buildImageUrl(group.image, 'json-ld');
    }
  }, [group]);

  const featuredIds = useMemo(() => {
    return new Set(group?.featuredUpcomingEvents?.map((e: { id: string }) => e.id) ?? []);
  }, [group?.featuredUpcomingEvents]);

  const filteredUpcomingEvents = useMemo(() => {
    if (!group?.upcomingEvents) return [];
    if (featuredIds.size === 0) return group.upcomingEvents;
    return group.upcomingEvents.filter((e: { id: string }) => !featuredIds.has(e.id));
  }, [group, featuredIds]);

  if (groupInviteLoading) { return <Loading />; }
  if (groupInviteError) {
    return (
      <ErrorMessage message='An error occurred while loading invite.' error={groupInviteError} />
    );
  }
  const membersCount = group.membersCount;
  const moreUpcomingEventsCount = group.upcomingEventsCount - (group.upcomingEvents?.length ?? 0);

  return (
    <>
      {group.archivedAt && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            This group has been archived. No new events, posts, or members can be added.
          </Typography>
          {group.permissions.editable && (
            <UnarchiveGroupButton groupId={group.id} slug={group.slug} />
          )}
        </Alert>
      )}

      <div className={classes.main}>
        <div className={classes.about}>
          {group.description && (
            <div className={classes.description}>
              <Markdown>{group.description}</Markdown>
            </div>
          )}
          {group.memberDescription &&
            group.membership &&
            _includes(['OWNER', 'MEMBER'], group.membership.role) && (
            <div className={classes.description}>
              <Markdown>{group.memberDescription}</Markdown>
            </div>
          )}

          <SocialLinks source={group} />

          <div className={classes.chipsWrapper}>
            {_isFinite(membersCount) && (
              <Chip
                size="small"
                label={`${membersCount} member${membersCount !== 1 ? 's' : ''}`}
                variant="outlined"
                component={Link}
                href={`/groups/${group.slug}/members`}
                clickable
              />
            )}
            <Chip
              size="small"
              label={`${group.pastEventsCount} past event${group.pastEventsCount !== 1 ? 's' : ''}`}
              variant="outlined"
              component={Link}
              href={`/groups/${group.slug}/calendar/past`}
              clickable
            />
          </div>

          {!group.archivedAt && (
            groupInvite && !group.membership ? (
              <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                <div>You&lsquo;ve been invited to join {group.name}!</div>
                <JoinButton
                  group={group}
                  groupInvite={groupInvite}
                  onJoin={() => {
                    refetch({ slug: group.slug });
                  }}
                  className={classes.inviteButton}
                />
              </Alert>
            ) : (
              <div className={classes.buttons}>
                <JoinButton
                  group={group}
                  groupInvite={groupInvite}
                  onJoin={() => {
                    refetch({ slug: group.slug });
                  }}
                />

                {group.permissions.editable && (
                  <Fragment>
                    <MaterialLinkButton
                      href={`/groups/${group.slug}/edit`}
                      variant="outlined"
                    >
                      Edit Group
                    </MaterialLinkButton>

                    <Badge badgeContent={group.pendingMembersCount} color="error">
                      <MaterialLinkButton
                        href={`/groups/${group.slug}/members`}
                        variant="outlined"
                      >
                        Manage Members
                      </MaterialLinkButton>
                    </Badge>

                    <MaterialLinkButton
                      href="/event-series"
                      variant="outlined"
                      startIcon={<EventRepeatIcon />}
                    >
                      Manage series
                    </MaterialLinkButton>
                  </Fragment>
                )}
              </div>
            )
          )}
        </div>

        {group.image && (
          <div className={classes.image}>
            <ImageUpload record={group} size="300x300iwe" linkToFull />
          </div>
        )}
      </div>

      {!group.found && (
        <Fragment>
          <SectionHeader sx={{ mt: 3 }}>
            Upcoming
          </SectionHeader>

          {group.featuredUpcomingEvents.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'secondary.main' }}>
              <Typography variant="h3" className={classes.featuredHeader}>
                <Star className={classes.featuredIcon} />
                Featured
              </Typography>

              <EventList
                events={group.featuredUpcomingEvents}
                group={group}
                fullDate
                className={classes.eventList}
              />
            </Paper>
          )}

          <DatedEventList
            events={filteredUpcomingEvents}
            timezone={group.city?.timezone}
            group={group}
            fullDate
            disableContainer
            emptyMessage={
              group.permissions.joined ? 'No upcoming events' : 'No upcoming public events'
            }
          />

          <MaterialLinkButton
            href={`/groups/${group.slug}/calendar`}
            variant="outlined"
            sx={{ mt: filteredUpcomingEvents.length > 0 ? 2 : 0, mb: 0 }}
          >
            View Full Calendar{moreUpcomingEventsCount > 0 && ` (${moreUpcomingEventsCount} more)`}
          </MaterialLinkButton>

          <EventSeriesSection
            eventSeries={group.eventSeries ?? []}
            editable={!!group.permissions.editable}
          />

          <LatestPost post={_get(group.latestPost, 0)} group={group} />

          <GroupImageUploads
            group={group}
            imageUploadsCount={group.imageUploadsCount}
            slug={group.slug}
          />
        </Fragment>
      )}
    </>
  );
};

export default GroupDetailPageClient;
